import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

const API_BASE = "/api";

// =============================================================================
// Types
// =============================================================================

export interface OrganizationMetadata {
  id: string;
  keycloakOrgId: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  maxMembers: number | null;
  storageQuotaBytes: number | null;
  metadata: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface KeycloakOrg {
  id: string;
  name: string;
  alias: string;
  enabled: boolean;
  description?: string;
  attributes?: Record<string, string[]>;
  domains?: Array<{ name: string; verified: boolean }>;
  metadata: OrganizationMetadata | null;
  membership?: { name: string; roles: string[] };
}

export interface OrgMember {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  orgRoles: string[];
}

export interface OrgsListResponse {
  organizations: KeycloakOrg[];
}

export interface OrgMembersResponse {
  members: OrgMember[];
}

// =============================================================================
// Fetch helper
// =============================================================================

async function fetchOrgAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

// =============================================================================
// API
// =============================================================================

export const organizationsApi = {
  list: (params?: { search?: string; first?: number; max?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set("search", params.search);
    if (params?.first !== undefined) qs.set("first", String(params.first));
    if (params?.max !== undefined) qs.set("max", String(params.max));
    const query = qs.toString();
    return fetchOrgAPI<OrgsListResponse>(`/organizations${query ? `?${query}` : ""}`);
  },

  get: (id: string) => fetchOrgAPI<KeycloakOrg>(`/organizations/${id}`),

  create: (data: {
    name: string;
    slug: string;
    description?: string;
    plan?: string;
    maxMembers?: number;
    storageQuotaBytes?: number;
    domains?: string[];
    metadata?: Record<string, unknown>;
  }) => fetchOrgAPI<{ id: string; keycloakOrgId: string }>("/organizations", {
    method: "POST",
    body: JSON.stringify(data),
  }),

  update: (id: string, data: {
    name?: string;
    description?: string;
    plan?: string;
    maxMembers?: number | null;
    storageQuotaBytes?: number | null;
    metadata?: Record<string, unknown>;
  }) => fetchOrgAPI<{ ok: boolean }>(`/organizations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  }),

  delete: (id: string) => fetchOrgAPI<{ ok: boolean }>(`/organizations/${id}`, {
    method: "DELETE",
  }),

  // Members
  listMembers: (id: string, params?: { first?: number; max?: number }) => {
    const qs = new URLSearchParams();
    if (params?.first !== undefined) qs.set("first", String(params.first));
    if (params?.max !== undefined) qs.set("max", String(params.max));
    const query = qs.toString();
    return fetchOrgAPI<OrgMembersResponse>(`/organizations/${id}/members${query ? `?${query}` : ""}`);
  },

  addMember: (orgId: string, userId: string) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/members`, {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),

  invite: (orgId: string, email: string) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/invite`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  removeMember: (orgId: string, userId: string) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/members/${userId}`, {
      method: "DELETE",
    }),

  changeMemberRole: (orgId: string, userId: string, grant?: Array<{ id: string; name: string }>, revoke?: Array<{ id: string; name: string }>) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/members/${userId}/role`, {
      method: "PATCH",
      body: JSON.stringify({ grant, revoke }),
    }),

  // Roles
  listRoles: (orgId: string) =>
    fetchOrgAPI<{ roles: Array<{ id: string; name: string; description?: string }> }>(`/organizations/${orgId}/roles`),

  createRole: (orgId: string, data: { name: string; description?: string }) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/roles`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  deleteRole: (orgId: string, roleName: string) =>
    fetchOrgAPI<{ ok: boolean }>(`/organizations/${orgId}/roles/${encodeURIComponent(roleName)}`, {
      method: "DELETE",
    }),
};
